<script setup>
// Consentimento OAuth do fleapchat-mcp. O MCP manda o navegador pra cá com o id do
// pedido, o nome do conector, os escopos e a URL de retorno. O usuário (já logado)
// escolhe a conta, autoriza, e a gente volta pro MCP com um handoff de uso único.
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import McpOauthAPI from 'dashboard/api/mcpOauth';

const { t } = useI18n();
const accounts = useMapGetter('getUserAccounts');

const query = new URLSearchParams(window.location.search);
const pedido = query.get('pedido') || '';
const cliente = query.get('cliente') || 'Aplicativo';
const escopos = (query.get('escopos') || '').split(',').filter(Boolean);
const retorno = query.get('retorno') || '';

// Só volta pro MCP configurado em MCP_OAUTH_URL: evita virar redirect aberto.
const retornoValido = computed(() => {
  try {
    const base = new URL(window.chatwootConfig?.mcpOauthUrl || '');
    const url = new URL(retorno);
    return url.host === base.host && url.protocol === base.protocol;
  } catch {
    return false;
  }
});
const pedidoValido = computed(() => Boolean(pedido) && retornoValido.value);

const contaId = ref(null);
onMounted(() => {
  const lista = accounts.value || [];
  if (lista.length) contaId.value = lista[0].id;
});
const conta = computed(() =>
  (accounts.value || []).find(a => a.id === Number(contaId.value))
);
const isAdmin = computed(() => conta.value?.role === 'administrator');

const permissoes = computed(() => {
  const textos = {
    read: t('APP_GLOBAL.MCP_CONECTAR.SCOPE_READ'),
    'crm:write': t('APP_GLOBAL.MCP_CONECTAR.SCOPE_CRM_WRITE'),
    'messages:write': t('APP_GLOBAL.MCP_CONECTAR.SCOPE_MESSAGES_WRITE'),
  };
  return escopos.map(e => textos[e]).filter(Boolean);
});
const podeMandarMensagem = computed(() => escopos.includes('messages:write'));

const enviando = ref(false);
const erro = ref('');

const voltarParaMcp = params => {
  const url = new URL(retorno);
  url.searchParams.set('pedido', pedido);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  window.location.assign(url.href);
};

const autorizar = async () => {
  if (!isAdmin.value || enviando.value) return;
  enviando.value = true;
  erro.value = '';
  try {
    const { data } = await McpOauthAPI.criarHandoff({
      pedidoId: pedido,
      accountId: conta.value.id,
    });
    voltarParaMcp({ handoff: data.handoff });
  } catch (e) {
    erro.value = e?.response?.data?.error || t('APP_GLOBAL.MCP_CONECTAR.ERROR');
    enviando.value = false;
  }
};

const cancelar = () => voltarParaMcp({ error: 'access_denied' });
</script>

<template>
  <div
    class="flex flex-col flex-1 items-center justify-center w-full h-full p-6 bg-n-slate-2"
  >
    <div
      class="w-full max-w-md p-8 bg-n-solid-1 rounded-xl border border-n-weak shadow-sm"
    >
      <template v-if="pedidoValido">
        <h1 class="mb-2 text-xl font-medium text-n-slate-12">
          {{ $t('APP_GLOBAL.MCP_CONECTAR.TITLE', { cliente }) }}
        </h1>
        <p class="mb-6 text-sm text-n-slate-11">
          {{ $t('APP_GLOBAL.MCP_CONECTAR.DESCRIPTION', { cliente }) }}
        </p>

        <label class="block mb-6">
          <span class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('APP_GLOBAL.MCP_CONECTAR.ACCOUNT') }}
          </span>
          <select
            v-model="contaId"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option v-for="a in accounts" :key="a.id" :value="a.id">
              {{ a.name }}
            </option>
          </select>
        </label>

        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('APP_GLOBAL.MCP_CONECTAR.ALLOWED') }}
        </p>
        <ul
          class="mb-2 space-y-1 text-sm text-n-slate-11 list-disc list-inside"
        >
          <li v-for="p in permissoes" :key="p">{{ p }}</li>
        </ul>
        <p v-if="!podeMandarMensagem" class="mb-6 text-sm text-n-slate-11">
          {{ $t('APP_GLOBAL.MCP_CONECTAR.NOT_ALLOWED') }}
        </p>

        <p
          v-if="conta && !isAdmin"
          class="p-3 mb-6 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
        >
          {{ $t('APP_GLOBAL.MCP_CONECTAR.ADMIN_ONLY', { conta: conta.name }) }}
        </p>
        <p v-if="erro" class="mb-4 text-sm text-n-ruby-11">{{ erro }}</p>

        <div class="flex justify-end gap-2">
          <NextButton
            variant="faded"
            :label="$t('APP_GLOBAL.MCP_CONECTAR.CANCEL')"
            @click="cancelar"
          />
          <NextButton
            :label="$t('APP_GLOBAL.MCP_CONECTAR.AUTHORIZE')"
            :disabled="!isAdmin || enviando"
            :is-loading="enviando"
            @click="autorizar"
          />
        </div>
      </template>

      <p v-else class="text-sm text-n-slate-11">
        {{ $t('APP_GLOBAL.MCP_CONECTAR.INVALID') }}
      </p>
    </div>
  </div>
</template>
